import supabase from "@/services/supabaseClient";
import { getCurrentPatientId } from "@/services/appointmentService.js";
import {
  getFolaMessagingIdentity,
  isFolaMessagingIdentity,
  resolveMessagingParticipantAvatar,
  resolveMessagingParticipantName,
} from "@/utils/messagingIdentity.js";
import { resolvePatientFacingDoctorName } from "@/utils/doctorPatientFacingName.js";
import { loadProfessionalConnections } from "@/services/referralPackageService.js";

export const MESSAGES_CHANGED_EVENT = "afrimed:messages:changed";

function dispatchMessagesChanged(detail = {}) {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(MESSAGES_CHANGED_EVENT, { detail }));
}

function fullNameFromRow(row) {
  return [
    String(row?.first_name || '').trim(),
    String(row?.last_name || '').trim(),
  ]
    .filter(Boolean)
    .join(' ')
    .trim();
}

function firstText(...values) {
  for (const value of values) {
    const text = String(value || '').trim();
    if (text) return text;
  }
  return '';
}

function normalizeRole(value) {
  return String(value || "").trim().toLowerCase();
}

function isFolaIdentity(value) {
  return isFolaMessagingIdentity(value);
}

function extractPrimarySpecialty(profile) {
  const doctorProfile =
    profile?.doctor_profile && typeof profile.doctor_profile === "object"
      ? profile.doctor_profile
      : {};
  const publicDoctor =
    profile?.public_doctor && typeof profile.public_doctor === "object"
      ? profile.public_doctor
      : {};

  const specialties = Array.isArray(doctorProfile?.specialties)
    ? doctorProfile.specialties
    : [];

  return firstText(
    publicDoctor?.specialty,
    doctorProfile?.primary_specialty,
    doctorProfile?.specialization,
    doctorProfile?.specialty,
    specialties[0],
  );
}

function inferRecipientRole(profile) {
  const explicitRole = normalizeRole(profile?.role);
  if (explicitRole) return explicitRole;
  if (profile?.doctor_profile || profile?.public_doctor) return "doctor";
  if (profile?.patient_profile) return "patient";
  return "";
}

function buildRecipientSubtitle(profile) {
  if (isFolaIdentity(profile?.display_name) || isFolaIdentity(profile?.full_name) || isFolaIdentity(profile?.title)) {
    return getFolaMessagingIdentity().subtitle;
  }

  const role = inferRecipientRole(profile);
  if (role === "doctor") {
    return extractPrimarySpecialty(profile) || "Doctor";
  }
  if (role === "patient") {
    return "Patient";
  }
  return "Recipient";
}

function buildFolaRecipient(conversationId = null) {
  const fola = getFolaMessagingIdentity();
  return {
    userId: "fola",
    title: fola.title,
    subtitle: fola.subtitle,
    avatarUrl: fola.avatarUrl,
    role: "assistant",
    conversationId: conversationId || "fola",
    hasExistingConversation: true,
    type: "fola",
  };
}

function uniqueChannelName(prefix) {
  const suffix = Math.random().toString(36).slice(2, 10);
  return `${prefix}-${suffix}`;
}

function buildMessageNotificationType(conversationId, senderId, messageId) {
  return `message_thread:${conversationId}:${senderId}:${messageId}`;
}

function truncateMessagePreview(content, fallback = "Open to view the latest message.") {
  const text = String(content || "").trim() || fallback;
  if (text.length <= 120) return text;
  return `${text.slice(0, 117).trimEnd()}...`;
}

function safeParse(raw) {
  try {
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function sortMessagesByCreatedAt(messages = []) {
  return [...(Array.isArray(messages) ? messages : [])].sort((left, right) => {
    const leftTime = new Date(left?.created_at || 0).getTime();
    const rightTime = new Date(right?.created_at || 0).getTime();
    if (!Number.isFinite(leftTime) && !Number.isFinite(rightTime)) return 0;
    if (!Number.isFinite(leftTime)) return -1;
    if (!Number.isFinite(rightTime)) return 1;
    return leftTime - rightTime;
  });
}

function readCachedPatientAvatarMap() {
  const map = new Map();
  if (typeof window === 'undefined') return map;

  const accept = (userId, avatarUrl) => {
    const nextUserId = String(userId || '').trim();
    const nextAvatarUrl = String(avatarUrl || '').trim();
    if (!nextUserId || !nextAvatarUrl || map.has(nextUserId)) return;
    map.set(nextUserId, nextAvatarUrl);
  };

  const scanPayload = (payload) => {
    if (!payload || typeof payload !== 'object') return;

    accept(
      payload?.patient_user_id ||
        payload?.patient?.user_id ||
        payload?.restored?.patient_user_id ||
        payload?.restored?.patient?.user_id ||
        payload?.restored?.patientProfile?.user_id,
      firstText(
        payload?.patient_avatar,
        payload?.patient?.avatar_url,
        payload?.patient?.profile_image_url,
        payload?.restored?.patient_avatar,
        payload?.restored?.patient?.avatar_url,
        payload?.restored?.patient?.profile_image_url,
        payload?.restored?.patientProfile?.avatar_url,
        payload?.restored?.patientProfile?.profile_image_url,
      )
    );
  };

  const scanStorage = (storage) => {
    try {
      for (let index = 0; index < storage.length; index += 1) {
        const key = String(storage.key(index) || '').trim();
        if (
          !key ||
          (!key.startsWith('afrimed:doctor_appt:') &&
            !key.startsWith('afrimed:appointment:'))
        ) {
          continue;
        }

        scanPayload(safeParse(storage.getItem(key)));
      }
    } catch {
      // ignore storage access issues
    }
  };

  scanStorage(window.sessionStorage);
  scanStorage(window.localStorage);
  return map;
}

async function resolveStorageAvatarUrl(row, bucket) {
  const direct = String(
    row?.resolved_avatar_url ||
      row?.profile_image_url ||
      row?.profile_photo_url ||
      row?.photo_url ||
      row?.avatar_url ||
      ''
  ).trim();
  if (direct) return direct;

  const path = String(
    row?.profile_photo_path ||
      row?.profile_photo_storage_path ||
      row?.photo_path ||
      row?.avatar_path ||
      ''
  ).trim();
  if (!path) return '';
  if (/^https?:\/\//i.test(path)) return path;

  try {
    const storage = supabase.storage.from(bucket);
    const { data, error } = await storage.createSignedUrl(path, 60 * 60 * 24);
    if (!error && data?.signedUrl) return data.signedUrl;

    const { data: publicData } = storage.getPublicUrl(path);
    return String(publicData?.publicUrl || '').trim();
  } catch {
    return direct;
  }
}

class MessagingService {
  async createMessageNotifications({ conversation, message, senderId }) {
    // Message activity now lives in the Messages badge/thread model, not Notifications.
    // Keep the method as a no-op so older call sites fail safely if they survive a refactor.
    return;
  }

  async loadProfilesMap(userIds = []) {
    try {
      const ids = Array.from(
        new Set(
          (Array.isArray(userIds) ? userIds : [userIds])
            .map((value) => String(value || "").trim())
            .filter(Boolean)
        )
      );

      if (ids.length === 0) return new Map();

      const [byUserId, byId, doctorProfiles, patientProfiles] = await Promise.all([
        supabase?.from("profiles")?.select("id,user_id,display_name,avatar_url,role")?.in("user_id", ids),
        supabase?.from("profiles")?.select("id,user_id,display_name,avatar_url,role")?.in("id", ids),
        supabase?.from("doctor_profiles")?.select("*")?.in("user_id", ids),
        supabase?.from("patient_profiles")?.select("*")?.in("user_id", ids),
      ]);
      const { data: publicDoctors } = await supabase
        ?.from("v_doctor_directory_browse")
        ?.select("doctor_user_id,display_name,profile_photo_path")
        ?.in("doctor_user_id", ids);

      const map = new Map();
      const doctorRows = await Promise.all(
        (doctorProfiles?.data || []).map(async (row) => ({
          ...row,
          resolved_avatar_url: await resolveStorageAvatarUrl(row, 'doctor-profile-images'),
        }))
      );
      const cachedPatientAvatarByUserId = readCachedPatientAvatarMap();
      const patientRows = await Promise.all(
        (patientProfiles?.data || []).map(async (row) => ({
          ...row,
          resolved_avatar_url:
            (await resolveStorageAvatarUrl(row, 'patient-profile-images')) ||
            cachedPatientAvatarByUserId.get(String(row?.user_id || '').trim()) ||
            '',
        }))
      );
      const publicDoctorRows = await Promise.all(
        (publicDoctors || []).map(async (row) => ({
          ...row,
          resolved_avatar_url: await resolveStorageAvatarUrl(row, 'doctor-profile-images'),
        }))
      );
      const doctorByUserId = new Map();
      const patientByUserId = new Map();
      const publicDoctorByUserId = new Map();

      doctorRows.forEach((row) => {
        const userId = String(row?.user_id || '').trim();
        if (!userId || doctorByUserId.has(userId)) return;
        doctorByUserId.set(userId, row);
      });

      patientRows.forEach((row) => {
        const userId = String(row?.user_id || '').trim();
        if (!userId || patientByUserId.has(userId)) return;
        patientByUserId.set(userId, row);
      });

      publicDoctorRows.forEach((row) => {
        const userId = String(row?.doctor_user_id || '').trim();
        if (!userId || publicDoctorByUserId.has(userId)) return;
        publicDoctorByUserId.set(userId, row);
      });

      [...(byUserId?.data || []), ...(byId?.data || [])].forEach((row) => {
        const userId = String(row?.user_id || '').trim();
        const rowId = String(row?.id || '').trim();
        const doctorProfile = doctorByUserId.get(userId) || null;
        const patientProfile = patientByUserId.get(userId) || null;
        const publicDoctor = publicDoctorByUserId.get(userId) || null;
        const hydratedRow = {
          ...row,
          doctor_profile: doctorProfile,
          patient_profile: patientProfile,
          public_doctor: publicDoctor,
          avatar_url:
            row?.avatar_url ||
            publicDoctor?.resolved_avatar_url ||
            doctorProfile?.resolved_avatar_url ||
            doctorProfile?.profile_image_url ||
            patientProfile?.resolved_avatar_url ||
            cachedPatientAvatarByUserId.get(userId) ||
            patientProfile?.profile_image_url ||
            patientProfile?.avatar_url ||
            '',
        };
        if (userId && !map.has(userId)) map.set(userId, hydratedRow);
        if (rowId && !map.has(rowId)) map.set(rowId, hydratedRow);
      });

      doctorByUserId.forEach((doctorProfile, userId) => {
        if (!userId || map.has(userId)) return;
        const publicDoctor = publicDoctorByUserId.get(userId) || null;
        map.set(userId, {
          id: userId,
          user_id: userId,
          display_name: resolvePatientFacingDoctorName(
            doctorProfile,
            doctorProfile?.full_name,
            publicDoctor?.display_name
          ),
          role: 'doctor',
          avatar_url:
            publicDoctor?.resolved_avatar_url ||
            doctorProfile?.resolved_avatar_url ||
            doctorProfile?.profile_image_url ||
            '',
          doctor_profile: doctorProfile,
          patient_profile: null,
          public_doctor: publicDoctor,
        });
      });

      publicDoctorByUserId.forEach((publicDoctor, userId) => {
        if (!userId || map.has(userId)) return;
        map.set(userId, {
          id: userId,
          user_id: userId,
          display_name: publicDoctor?.display_name || '',
          role: 'doctor',
          avatar_url: publicDoctor?.resolved_avatar_url || '',
          doctor_profile: null,
          patient_profile: null,
          public_doctor: publicDoctor,
        });
      });

      patientByUserId.forEach((patientProfile, userId) => {
        if (!userId || map.has(userId)) return;
        map.set(userId, {
          id: userId,
          user_id: userId,
          display_name: resolveMessagingParticipantName(
            { user_id: userId, profiles: { role: "patient", patient_profile: patientProfile } },
            patientProfile?.full_name || fullNameFromRow(patientProfile) || "",
          ),
          role: 'patient',
          avatar_url:
            patientProfile?.resolved_avatar_url ||
            cachedPatientAvatarByUserId.get(userId) ||
            patientProfile?.profile_image_url ||
            patientProfile?.avatar_url ||
            '',
          doctor_profile: null,
          patient_profile: patientProfile,
          public_doctor: null,
        });
      });

      cachedPatientAvatarByUserId.forEach((avatarUrl, userId) => {
        if (!userId || map.has(userId)) return;
        map.set(userId, {
          id: userId,
          user_id: userId,
          display_name: '',
          role: 'patient',
          avatar_url: avatarUrl,
          doctor_profile: null,
          patient_profile: null,
          public_doctor: null,
        });
      });

      return map;
    } catch (error) {
      console.error('Error loading messaging profiles:', error);
      return new Map();
    }
  }

  hydrateConversationProfiles(conversation, profilesMap) {
    if (!conversation || typeof conversation !== 'object') return conversation;

    const participants = Array.isArray(conversation?.conversation_participants)
      ? conversation.conversation_participants
      : [];
    const messages = sortMessagesByCreatedAt(conversation?.messages);

    return {
      ...conversation,
      conversation_participants: participants.map((participant) => ({
          ...participant,
          profiles: profilesMap.get(String(participant?.user_id || "").trim()) || null,
      })),
      messages: messages.map((message) => ({
        ...message,
        profiles: profilesMap.get(String(message?.sender_id || "").trim()) || null,
      })),
    };
  }

  async getCurrentUserId() {
    const { data } = await supabase?.auth?.getUser();
    return data?.user?.id || null;
  }

  // Check if messaging tables exist
  async checkMessagingTablesExist() {
    try {
      const { error } = await supabase?.from("conversations")?.select("id")?.limit(1);
      return !error || error?.code !== "PGRST205";
    } catch (error) {
      return false;
    }
  }

  // Conversation management
  async createConversation(type, title, description, participantIds) {
    try {
      const tablesExist = await this.checkMessagingTablesExist();
      if (!tablesExist) {
        throw new Error("Messaging system not available. Please ensure the database migration has been applied.");
      }

      const { data: user } = await supabase?.auth?.getUser();
      if (!user?.user?.id) throw new Error("User not authenticated");

      // Create conversation
      const { data: conversation, error: conversationError } = await supabase?.from("conversations")?.insert({
          type,
          title,
          description,
          created_by: user?.user?.id
        })?.select()?.single();

      if (conversationError) throw conversationError;

      // Add creator as admin participant
      const participants = [
        { conversation_id: conversation?.id, user_id: user?.user?.id, role: "admin" },
        ...participantIds?.map(userId => ({
          conversation_id: conversation?.id,
          user_id: userId,
          role: "member"
        }))
      ];

      const { error: participantsError } = await supabase?.from("conversation_participants")?.insert(participants);

      if (participantsError) throw participantsError;

      dispatchMessagesChanged({ kind: "conversation-created", conversationId: conversation?.id });
      return conversation;
    } catch (error) {
      console.error("Error creating conversation:", error);
      throw error;
    }
  }

  async getConversations() {
    try {
      const tablesExist = await this.checkMessagingTablesExist();
      if (!tablesExist) {
        console.warn("Messaging tables not found. Migration may need to be applied.");
        return [];
      }

      const currentUserId = await this.getCurrentUserId();
      if (!currentUserId) return [];

      const { data, error } = await supabase?.from("conversations")?.select(`
          *,
          conversation_participants!inner (
            user_id,
            role,
            last_read_at,
            is_muted
          ),
          messages (
            id,
            content,
            created_at,
            sender_id,
            status,
            is_priority
          )
        `)?.order("updated_at", { ascending: false });

      if (error) {
        if (error?.code === "PGRST205") {
          console.warn("Conversations table not found. Migration may need to be applied.");
          return [];
        }
        throw error;
      }

      const profileIds = (data || []).flatMap((conversation) => [
        ...(Array.isArray(conversation?.conversation_participants)
          ? conversation.conversation_participants.map((participant) => participant?.user_id)
          : []),
        ...(Array.isArray(conversation?.messages)
          ? conversation.messages.map((message) => message?.sender_id)
          : []),
      ]);
      const profilesMap = await this.loadProfilesMap(profileIds);

      return data?.map((row) => {
        const conversation = this.hydrateConversationProfiles(row, profilesMap);
        return ({
        ...conversation,
        lastMessage: conversation?.messages?.[conversation?.messages?.length - 1] || null,
        unreadCount: this.calculateUnreadCount(
          conversation?.messages,
          conversation?.conversation_participants,
          currentUserId
        )
      })}) || [];
    } catch (error) {
      console.error("Error fetching conversations:", error);
      return [];
    }
  }

  async getConversationById(conversationId) {
    try {
      const tablesExist = await this.checkMessagingTablesExist();
      if (!tablesExist) {
        throw new Error("Messaging system not available. Please ensure the database migration has been applied.");
      }

      const { data, error } = await supabase?.from("conversations")?.select(`
          *,
          conversation_participants (
            user_id,
            role,
            last_read_at,
            is_muted
          )
        `)?.eq("id", conversationId)?.maybeSingle();

      if (error) throw error;
      if (!data) return null;

      const profileIds = (Array.isArray(data?.conversation_participants)
        ? data.conversation_participants.map((participant) => participant?.user_id)
        : []);
      const profilesMap = await this.loadProfilesMap(profileIds);
      return this.hydrateConversationProfiles(data, profilesMap);
    } catch (error) {
      console.error("Error fetching conversation:", error);
      throw error;
    }
  }

  async getOrCreateDirectConversation({
    otherUserId,
    title = '',
    description = '',
    type = "doctor_patient",
  }) {
    try {
      const currentUserId = await this.getCurrentUserId();
      const otherId = String(otherUserId || "").trim();
      if (!currentUserId) throw new Error("User not authenticated");
      if (!otherId) throw new Error("Recipient is required");
      if (otherId === currentUserId) throw new Error("Choose another participant");

      if (String(type || "doctor_patient").trim().toLowerCase() === "doctor_patient" && !String(description || "").trim()) {
        const { data, error } = await supabase.rpc("get_or_create_patient_clinician_conversation", {
          other_user_id: otherId,
          conversation_title: title || null,
        });
        if (error) throw error; // Never silently fall back to non-atomic creation.
        const thread = Array.isArray(data) ? data[0] : data;
        if (!thread?.id) throw new Error("Failed to resolve conversation");
        return await this.getConversationById(thread.id);
      }

      const conversations = await this.getConversations();
      const existing = conversations.find((conversation) => {
        const participants = Array.isArray(conversation?.conversation_participants)
          ? conversation.conversation_participants
          : [];
        const participantIds = participants.map((participant) => participant?.user_id).filter(Boolean);
        const conversationType = String(conversation?.type || "").trim().toLowerCase();
        const targetType = String(type || "doctor_patient").trim().toLowerCase();
        return (
          conversationType === targetType &&
          participantIds.includes(currentUserId) &&
          participantIds.includes(otherId) &&
          participantIds.length === 2
        );
      });

      if (existing?.id) return existing;

      const created = await this.createConversation(type, title || null, description || null, [otherId]);
      if (!created?.id) throw new Error("Failed to create conversation");
      return await this.getConversationById(created.id);
    } catch (error) {
      console.error("Error creating direct conversation:", error);
      throw error;
    }
  }

  async getComposeRecipients({ currentRole = "" } = {}) {
    try {
      const currentUserId = await this.getCurrentUserId();
      if (!currentUserId) return [];

      const normalizedRole = normalizeRole(currentRole);
      const recipientsByUserId = new Map();
      const conversations = await this.getConversations();

      const upsertRecipient = ({ userId, profiles = null, conversationId = null }) => {
        const nextUserId = String(userId || "").trim();
        if (!nextUserId || nextUserId === currentUserId) return;

        const participant = {
          user_id: nextUserId,
          profiles: profiles || null,
        };
        const current = recipientsByUserId.get(nextUserId) || null;
        const nextTitle = resolveMessagingParticipantName(participant, "Recipient");
        const nextAvatarUrl = resolveMessagingParticipantAvatar(participant);
        const nextSubtitle = buildRecipientSubtitle(profiles || {});
        const nextRole = inferRecipientRole(profiles || {});
        const isFolaRecipient =
          isFolaIdentity(nextUserId) || isFolaIdentity(nextTitle) || isFolaIdentity(profiles?.display_name);
        const fola = getFolaMessagingIdentity();

        recipientsByUserId.set(nextUserId, {
          userId: nextUserId,
          title: isFolaRecipient ? fola.title : nextTitle,
          subtitle: isFolaRecipient ? fola.subtitle : nextSubtitle,
          avatarUrl: isFolaRecipient ? fola.avatarUrl : nextAvatarUrl,
          role: isFolaRecipient ? "assistant" : nextRole,
          conversationId: current?.conversationId || conversationId || null,
          hasExistingConversation:
            Boolean(current?.hasExistingConversation) || Boolean(conversationId),
          type: isFolaRecipient ? "fola" : undefined,
        });
      };

      conversations.forEach((conversation) => {
        const conversationTitle = resolveMessagingParticipantName(
          {
            user_id: "",
            profiles: null,
          },
          conversation?.title || "Conversation",
        );

        if (
          isFolaIdentity(conversation?.type) ||
          isFolaIdentity(conversation?.title) ||
          isFolaIdentity(conversationTitle)
        ) {
          recipientsByUserId.set("fola", buildFolaRecipient(conversation?.id || "fola"));
          return;
        }

        const participants = Array.isArray(conversation?.conversation_participants)
          ? conversation.conversation_participants
          : [];
        const otherParticipant = participants.find(
          (participant) => String(participant?.user_id || "").trim() !== currentUserId
        );

        if (!otherParticipant?.user_id) return;
        upsertRecipient({
          userId: otherParticipant.user_id,
          profiles: otherParticipant?.profiles || null,
          conversationId: conversation?.id || null,
        });
      });

      let appointmentRecipientUserIds = [];
      if (normalizedRole === "doctor") {
        const { data, error } = await supabase
          ?.from("appointments")
          ?.select(`
            patient:patients (
              id,
              user_id
            )
          `)
          ?.eq("doctor_id", currentUserId)
          ?.order("appointment_date", { ascending: false })
          ?.limit(200);

        if (error) throw error;

        appointmentRecipientUserIds = (Array.isArray(data) ? data : [])
          .map((row) => {
            const patient = Array.isArray(row?.patient) ? row.patient[0] || null : row?.patient || null;
            return String(patient?.user_id || "").trim();
          })
          .filter(Boolean);
      } else {
        const patientId = await getCurrentPatientId().catch(() => "");
        if (patientId) {
          const { data, error } = await supabase
            ?.from("appointments")
            ?.select("doctor_id")
            ?.eq("patient_id", patientId)
            ?.order("appointment_date", { ascending: false })
            ?.limit(200);

          if (error) throw error;

          appointmentRecipientUserIds = (Array.isArray(data) ? data : [])
            .map((row) => String(row?.doctor_id || "").trim())
            .filter(Boolean);
        }
      }

      const missingUserIds = Array.from(
        new Set(
          appointmentRecipientUserIds.filter((userId) => !recipientsByUserId.has(String(userId || "").trim()))
        )
      );

      if (missingUserIds.length > 0) {
        const profilesMap = await this.loadProfilesMap(missingUserIds);
        missingUserIds.forEach((userId) => {
          upsertRecipient({
            userId,
            profiles: profilesMap.get(String(userId || "").trim()) || null,
            conversationId: null,
          });
        });
      }

      // An accepted Referral Pool connection is a narrow professional
      // messaging eligibility.  It is not an appointment, referral-package,
      // or Patient-record projection.
      if (normalizedRole === "doctor") {
        const connections = await loadProfessionalConnections();
        connections
          .filter((connection) => normalizeRole(connection?.connection_status) === "accepted")
          .forEach((connection) => {
            const userId = String(connection?.doctor_user_id || "").trim();
            if (!userId || recipientsByUserId.has(userId)) return;
            upsertRecipient({
              userId,
              profiles: {
                user_id: userId,
                role: "doctor",
                display_name: connection?.display_name || "Clinician",
                public_doctor: {
                  specialty: connection?.specialty || "",
                  profile_photo_path: connection?.profile_photo_path || "",
                },
              },
            });
          });
      }

      return Array.from(recipientsByUserId.values()).sort((left, right) => {
        if (isFolaIdentity(left?.title) !== isFolaIdentity(right?.title)) {
          return isFolaIdentity(left?.title) ? -1 : 1;
        }

        if (left.hasExistingConversation !== right.hasExistingConversation) {
          return left.hasExistingConversation ? -1 : 1;
        }

        return String(left.title || "").localeCompare(String(right.title || ""), undefined, {
          sensitivity: "base",
        });
      });
    } catch (error) {
      console.error("Error loading compose recipients:", error);
      return [];
    }
  }

  // Message management
  async sendMessage(conversationId, content, messageType = "text", replyToId = null, isPriority = false) {
    try {
      const tablesExist = await this.checkMessagingTablesExist();
      if (!tablesExist) {
        throw new Error("Messaging system not available. Please ensure the database migration has been applied.");
      }

      const { data: user } = await supabase?.auth?.getUser();
      if (!user?.user?.id) throw new Error("User not authenticated");

      const { data, error } = await supabase?.from("messages")?.insert({
          conversation_id: conversationId,
          sender_id: user?.user?.id,
          content,
          message_type: messageType,
          reply_to_id: replyToId,
          is_priority: isPriority,
          status: "sent"
        })?.select('*')?.single();

      if (error) throw error;

      // Updating the parent conversation timestamp is helpful for inbox ordering,
      // but it should never block a successfully inserted message.
      const { error: touchError } = await supabase
        ?.from("conversations")
        ?.update({ updated_at: new Date()?.toISOString() })
        ?.eq("id", conversationId);

      if (touchError) {
        console.warn("Non-fatal conversation timestamp update error:", touchError);
      }

      dispatchMessagesChanged({
        kind: "message-sent",
        conversationId,
        messageId: data?.id,
      });
      return data;
    } catch (error) {
      console.error("Error sending message:", error);
      throw error;
    }
  }

  async getMessages(conversationId, limit = 50, offset = 0) {
    try {
      const tablesExist = await this.checkMessagingTablesExist();
      if (!tablesExist) {
        throw new Error("Messaging system not available. Please ensure the database migration has been applied.");
      }

      let data = null;
      let error = null;

      ({ data, error } = await supabase?.from("messages")?.select(`
          *,
          message_attachments (
            id,
            file_name,
            file_path,
            file_type,
            file_size,
            mime_type
          ),
          message_reactions (
            id,
            reaction,
            user_id
          )
        `)?.eq("conversation_id", conversationId)?.order("created_at", { ascending: false })?.range(offset, offset + limit - 1));

      if (error) {
        console.warn("Rich message fetch failed, retrying with base query:", error);
        ({ data, error } = await supabase?.from("messages")?.select("*")?.eq("conversation_id", conversationId)?.order("created_at", { ascending: false })?.range(offset, offset + limit - 1));
      }

      if (error) throw error;
      const orderedRows = [...(data || [])].reverse();
      const profileIds = orderedRows.flatMap((message) => [
        message?.sender_id,
        ...(Array.isArray(message?.message_reactions)
          ? message.message_reactions.map((reaction) => reaction?.user_id)
          : []),
      ]);
      const profilesMap = await this.loadProfilesMap(profileIds);

      return orderedRows.map((message) => ({
        ...message,
        profiles: profilesMap.get(String(message?.sender_id || '').trim()) || null,
        message_reactions: (Array.isArray(message?.message_reactions)
          ? message.message_reactions
          : []
        ).map((reaction) => ({
          ...reaction,
          profiles:
            profilesMap.get(String(reaction?.user_id || '').trim()) || null,
        })),
      }));
    } catch (error) {
      console.error("Error fetching messages:", error);
      throw error;
    }
  }

  async updateMessage(messageId, content) {
    try {
      const { data, error } = await supabase?.from("messages")?.update({ content, updated_at: new Date()?.toISOString() })?.eq("id", messageId)?.select()?.single();

      if (error) throw error;
      dispatchMessagesChanged({ kind: "message-updated", messageId: data?.id });
      return data;
    } catch (error) {
      console.error("Error updating message:", error);
      throw error;
    }
  }

  async deleteMessage(messageId) {
    try {
      const { error } = await supabase?.from("messages")?.delete()?.eq("id", messageId);

      if (error) throw error;
      dispatchMessagesChanged({ kind: "message-deleted", messageId });
      return true;
    } catch (error) {
      console.error("Error deleting message:", error);
      throw error;
    }
  }

  async deleteMessageForEveryone(messageId) {
    try {
      const { data: user } = await supabase?.auth?.getUser();
      const currentUserId = String(user?.user?.id || "").trim();
      if (!currentUserId) throw new Error("User not authenticated");

      const { data: existingMessage, error: fetchError } = await supabase
        ?.from("messages")
        ?.select("id,sender_id,metadata")
        ?.eq("id", messageId)
        ?.single();

      if (fetchError) throw fetchError;
      if (String(existingMessage?.sender_id || "").trim() !== currentUserId) {
        throw new Error("Only the sender can delete this message for both.");
      }

      const existingMetadata =
        existingMessage?.metadata &&
        typeof existingMessage.metadata === "object" &&
        !Array.isArray(existingMessage.metadata)
          ? existingMessage.metadata
          : {};

      const now = new Date()?.toISOString();
      const nextMetadata = {
        ...existingMetadata,
        deleted_for_everyone: true,
        deleted_at: now,
        deleted_by: currentUserId,
      };

      const { data, error } = await supabase
        ?.from("messages")
        ?.update({
          content: "Message deleted",
          message_type: "deleted",
          metadata: nextMetadata,
          updated_at: now,
        })
        ?.eq("id", messageId)
        ?.eq("sender_id", currentUserId)
        ?.select("*")
        ?.single();

      if (error) throw error;
      dispatchMessagesChanged({ kind: "message-deleted-for-everyone", messageId });
      return data;
    } catch (error) {
      console.error("Error deleting message for everyone:", error);
      throw error;
    }
  }

  // Message reactions
  async addReaction(messageId, reaction) {
    try {
      const { data: user } = await supabase?.auth?.getUser();
      if (!user?.user?.id) throw new Error("User not authenticated");

      const { data, error } = await supabase?.from("message_reactions")?.upsert({
          message_id: messageId,
          user_id: user?.user?.id,
          reaction
        })?.select()?.single();

      if (error) throw error;
      dispatchMessagesChanged({ kind: "reaction-upserted", messageId });
      return data;
    } catch (error) {
      console.error("Error adding reaction:", error);
      throw error;
    }
  }

  async removeReaction(messageId, reaction) {
    try {
      const { data: user } = await supabase?.auth?.getUser();
      if (!user?.user?.id) throw new Error("User not authenticated");

      const { error } = await supabase?.from("message_reactions")?.delete()?.eq("message_id", messageId)?.eq("user_id", user?.user?.id)?.eq("reaction", reaction);

      if (error) throw error;
      dispatchMessagesChanged({ kind: "reaction-deleted", messageId });
      return true;
    } catch (error) {
      console.error("Error removing reaction:", error);
      throw error;
    }
  }

  // File attachments
  async uploadAttachment(file, messageId) {
    try {
      const { data: user } = await supabase?.auth?.getUser();
      if (!user?.user?.id) throw new Error("User not authenticated");

      const fileExt = file?.name?.split('.')?.pop();
      const fileName = `${user?.user?.id}/${messageId}/${Date.now()}.${fileExt}`;

      const { error: uploadError } = await supabase?.storage?.from("message-attachments")?.upload(fileName, file, {
          upsert: false
        });

      if (uploadError) throw uploadError;

      // Save attachment record
      const { data, error: dbError } = await supabase?.from("message_attachments")?.insert({
          message_id: messageId,
          file_name: file?.name,
          file_path: fileName,
          file_size: file?.size,
          file_type: this.getFileType(file?.type),
          mime_type: file?.type
        })?.select()?.single();

      if (dbError) throw dbError;
      dispatchMessagesChanged({ kind: "attachment-uploaded", messageId });
      return data;
    } catch (error) {
      console.error("Error uploading attachment:", error);
      throw error;
    }
  }

  async getAttachmentUrl(filePath) {
    try {
      const { data, error } = await supabase?.storage?.from("message-attachments")?.createSignedUrl(filePath, 3600);

      if (error) throw error;
      return data?.signedUrl;
    } catch (error) {
      console.error("Error getting attachment URL:", error);
      return null;
    }
  }

  // Real-time subscriptions
  subscribeToConversation(conversationId, onUpdate) {
    const channel = supabase?.channel(uniqueChannelName(`conversation-${conversationId}`))?.on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "messages",
          filter: `conversation_id=eq.${conversationId}`
        },
        (payload) => {
          onUpdate?.(payload);
        }
      )?.on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "conversation_participants",
          filter: `conversation_id=eq.${conversationId}`,
        },
        (payload) => {
          onUpdate?.(payload);
        }
      )?.on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "conversations",
          filter: `id=eq.${conversationId}`,
        },
        (payload) => {
          onUpdate?.(payload);
        }
      )?.on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "message_attachments",
        },
        (payload) => {
          onUpdate?.(payload);
        }
      )?.subscribe((status) => {
        // Reconcile after initial subscription/reconnect; no event replay assumption.
        if (status === "SUBSCRIBED") onUpdate?.({ kind: "subscription-ready" });
      });

    return channel;
  }

  subscribeToConversations(userIdOrOnUpdate, maybeOnUpdate) {
    const stableUserId =
      typeof userIdOrOnUpdate === "string" ? String(userIdOrOnUpdate || "").trim() : "";
    const onUpdate =
      typeof userIdOrOnUpdate === "function" ? userIdOrOnUpdate : maybeOnUpdate;

    const channel = supabase?.channel(uniqueChannelName(`conversations-${stableUserId || "all"}`))?.on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "conversations"
        },
        (payload) => {
          onUpdate?.(payload);
        }
      )?.on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "messages"
        },
        (payload) => {
          onUpdate?.(payload);
        }
      )?.on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "conversation_participants",
          ...(stableUserId ? { filter: `user_id=eq.${stableUserId}` } : {}),
        },
        (payload) => {
          onUpdate?.(payload);
        }
      )?.subscribe();

    return channel;
  }

  unsubscribe(channel) {
    if (channel) {
      supabase?.removeChannel(channel);
    }
  }

  // User search for starting conversations
  async searchUsers(query) {
    try {
      const { data, error } = await supabase?.from("profiles")?.select("user_id, display_name, avatar_url")?.ilike("display_name", `%${query}%`)?.limit(10);

      if (error) throw error;
      const rows = Array.isArray(data) ? data : [];
      const profiles = await this.loadProfilesMap(rows.map((row) => row?.user_id));
      return rows.map((row) => {
        const profile = profiles.get(String(row?.user_id || "").trim()) || null;
        return {
          ...row,
          display_name: profile
            ? resolveMessagingParticipantName({ user_id: row?.user_id, profiles: profile }, row?.display_name || "")
            : row?.display_name || "",
        };
      });
    } catch (error) {
      console.error("Error searching users:", error);
      return [];
    }
  }

  // Utility functions
  calculateUnreadCount(messages, participants, currentUserId) {
    if (!currentUserId) return 0;

    const incomingMessages = (Array.isArray(messages) ? messages : []).filter(
      (message) => message?.sender_id !== currentUserId
    );
    const userParticipant = participants?.find((p) => p?.user_id === currentUserId);
    if (!userParticipant?.last_read_at) return incomingMessages.length;

    const lastReadTime = new Date(userParticipant.last_read_at);
    return (
      incomingMessages.filter((message) => new Date(message.created_at) > lastReadTime).length ||
      0
    );
  }

  getFileType(mimeType) {
    if (mimeType?.startsWith('image/')) return 'image';
    if (mimeType?.startsWith('video/')) return 'video';
    if (mimeType?.startsWith('audio/')) return 'audio';
    return 'document';
  }

  // Mark messages as read
  async markMessagesAsRead(conversationId) {
    try {
      const tablesExist = await this.checkMessagingTablesExist();
      if (!tablesExist) {
        throw new Error("Messaging system not available. Please ensure the database migration has been applied.");
      }

      const { data: user } = await supabase?.auth?.getUser();
      if (!user?.user?.id) throw new Error("User not authenticated");

      const { error } = await supabase?.from("conversation_participants")?.update({ last_read_at: new Date()?.toISOString() })?.eq("conversation_id", conversationId)?.eq("user_id", user?.user?.id);

      if (error) throw error;
      dispatchMessagesChanged({ kind: "conversation-read", conversationId });
      return true;
    } catch (error) {
      console.error("Error marking messages as read:", error);
      return false;
    }
  }
}

export const messagingService = new MessagingService();
export default messagingService;
